import { useAgent } from '@copilotkit/react-core/v2'
import { useCallback, useEffect, useRef, useState } from 'react'

const RESPONSE_TIMEOUT_MS = 120_000 // 2 minutes

export function useHumanMode() {
  const [humanMode, setHumanMode] = useState(false)
  const [humanModeActive, setHumanModeActive] = useState(false)
  const [humanModeStartedAt, setHumanModeStartedAt] = useState<number | null>(null)
  const [showContactForm, setShowContactForm] = useState(false)
  const { agent } = useAgent()

  const baselineAssistantCountRef = useRef(0)
  const responseReceivedRef = useRef(false)
  const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollHandleRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const syncSilently = useCallback(async () => {
    if (!agent.threadId) return
    try {
      const response = await fetch(`/api/copilotkit?threadId=${agent.threadId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) return

      const threadState = await response.json()
      if (!threadState.values?.messages) return

      const mappedMessages = threadState.values.messages
        .filter((msg: unknown) => (msg as { type?: string }).type !== 'tool') // tool result messages contain raw JSON
        .map((msg: any) => {
          let role: 'user' | 'assistant' | 'system' = 'assistant'
          if (msg.type === 'human') role = 'user'
          else if (msg.type === 'system') role = 'system'

          let content = ''
          if (typeof msg.content === 'string') {
            content = msg.content
          } else if (Array.isArray(msg.content)) {
            msg.content.forEach((m: any) => {
              content += m.text ?? ''
            })
          }

          return content
            ? { id: msg.id || Math.random().toString(), role, content, createdAt: new Date() }
            : null
        })
        .filter(Boolean)

      // If the backend cleared human_mode (contact form submitted or Slack error),
      // clean up immediately regardless of whether a new message arrived.
      if (threadState.values?.human_mode === false) {
        responseReceivedRef.current = true
        setShowContactForm(false)
        setHumanModeActive(false)
        setHumanModeStartedAt(null)
        if (timeoutHandleRef.current) {
          clearTimeout(timeoutHandleRef.current)
          timeoutHandleRef.current = null
        }
        if (pollHandleRef.current) {
          clearInterval(pollHandleRef.current)
          pollHandleRef.current = null
        }
        // Sync CopilotKit's local agent state so future messages aren't sent
        // with a stale human_mode: true that would re-trigger human-mode routing.
        if (agent.state) {
          agent.setState({ ...agent.state, human_mode: false })
        }
      }

      const lastMessage = mappedMessages.at(-1)
      const currentIds = new Set((agent.messages ?? []).map((m: any) => m.id))

      if (lastMessage?.role === 'assistant' && !currentIds.has(lastMessage.id)) {
        agent.setMessages(mappedMessages)

        const newAssistantCount = mappedMessages.filter((m: any) => m.role === 'assistant').length
        if (newAssistantCount > baselineAssistantCountRef.current) {
          responseReceivedRef.current = true
          setShowContactForm(false)
          setHumanModeActive(false)
          setHumanModeStartedAt(null)
          if (timeoutHandleRef.current) {
            clearTimeout(timeoutHandleRef.current)
            timeoutHandleRef.current = null
          }
        }
      }
    } catch {
      // Network errors — ignore silently
    }
  }, [agent])

  useEffect(() => {
    const isNowHumanMode = Boolean(agent.state?.human_mode)

    if (!humanMode && isNowHumanMode) {
      baselineAssistantCountRef.current = (agent.messages ?? []).filter(
        (m: any) => m.role === 'assistant',
      ).length
      responseReceivedRef.current = false

      setHumanModeActive(true)
      setHumanModeStartedAt(Date.now())

      pollHandleRef.current = setInterval(syncSilently, 2000)

      timeoutHandleRef.current = setTimeout(() => {
        if (!responseReceivedRef.current) {
          // Stop polling first so syncSilently can't fire after reset-human-mode
          // and flip showContactForm back to false.
          if (pollHandleRef.current) {
            clearInterval(pollHandleRef.current)
            pollHandleRef.current = null
          }
          setShowContactForm(true)
          setHumanModeActive(false)
          setHumanModeStartedAt(null)
          if (agent.threadId) {
            fetch('/api/reset-human-mode', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ threadId: agent.threadId }),
            }).catch(() => {})
          }
          // Sync local agent state so the next CopilotKit run doesn't
          // re-send human_mode: true and override the backend reset.
          if (agent.state) {
            agent.setState({ ...agent.state, human_mode: false })
          }
        }
      }, RESPONSE_TIMEOUT_MS)
    }

    if (!isNowHumanMode && humanMode) {
      if (pollHandleRef.current) clearInterval(pollHandleRef.current)
      if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current)
      responseReceivedRef.current = false
      // Don't reset showContactForm here — in the normal flow syncSilently already
      // cleared it when the human agent responded; in the timeout flow it should
      // stay visible until the user dismisses it.
      setHumanModeActive(false)
      setHumanModeStartedAt(null)
    }

    setHumanMode(isNowHumanMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.state])

  useEffect(
    () => () => {
      if (pollHandleRef.current) clearInterval(pollHandleRef.current)
      if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current)
    },
    [],
  )

  return { showContactForm, setShowContactForm, humanModeActive, humanModeStartedAt }
}
